import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import { BINARY_DECOMPOSITION, VENDORED_DEPENDENCY_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../shared/fetchers';
import Locator from '../../../../shared/Locator';
import { ProjectSource } from '../../../../typings/enums';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useOnUnmount } from '../../../hooks';
import { useReleaseGroupAllProjects } from '../../../hooks/api/releaseGroups';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import { SidebarLayout } from '../../layouts';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import type { ProjectGroup, ProjectGroupRelease } from '../../../../shared/model-typings';
import type { ManuallyAddedDependencyRequestKind } from '../../../../typings/api';
import type { IAsync, Project, RouterProps } from '../../../types';

import DependenciesTable from './DependenciesTable';
import DependencyFilters from './filters';
import DependenciesHeader from './header';
import { useDependencyDrawer } from './hooks/useDependencyDrawer';
import useDependencyModals from './hooks/useDependencyModals';
import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import { useSetDependencyScope } from './hooks/useSetDependenciesScope';
import { useSyncStateAndURLParams } from './hooks/useSyncStateAndURLParams';
import AddDependency from './modals/AddDependency';
import BinaryMatchDetailsModalContainer from './modals/BinaryMatchDetailsModal';
import EditDependency from './modals/EditDependency';
import RemoveDependency, { type DependencyToRemove } from './modals/RemoveDependency';
import StreamBuild from './modals/StreamBuild';
import ViewRelationships from './modals/ViewRelationships';

import styles from './dependencies.module.less';

const countOptions = [
  25,
  50,
  75,
  100,
];

export type DependenciesV2Props = {
  router: InjectedRouter;
  project?: IAsync<Project>;
  projectGroup?: ProjectGroup;
  projectGroupRelease?: ProjectGroupRelease;
  onProjectChange?: () => void;
} & RouterProps;

const DependenciesV2: VoidFunctionComponent<DependenciesV2Props> = ({
  router,
  location,
  params,
  project,
  projectGroup,
  projectGroupRelease,
  onProjectChange = _.noop,
}) => {
  const dispatch = useDispatch();
  useSyncStateAndURLParams({ router, location, params });
  const modals = useDependencyModals();

  const { project_id: projectId, revision_id: revisionId } = params;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  // optional projectRevision used by the EditDependency modal
  const projectRevision = scope && scope.scope === 'project' ? scope.revision : undefined;
  const releaseGroupId = scope && scope.scope === 'releaseGroup' ? scope.projectGroupId : -1;

  const isContainerProject = (project && project?.data?.type === ProjectSource.Container) ?? false;
  const isBinaryProject = (project && project?.data?.type === ProjectSource.Binary) ?? false;

  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const isVendoredDepsEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const {
    data: releaseProjects,
  } = useReleaseGroupAllProjects(releaseGroupId, isBinaryDecompEnabled && releaseGroupId > 0);
  const isReleaseGroupWithBinaryProject = (
    isReleaseGroupScope &&
    releaseProjects?.some(releaseProject => {
      return Locator.parse(releaseProject).fetcher === BinaryFetcherName;
    })) ?? false;

  const page = useSelector(DependenciesV2Store.selectors.getPage);
  const count = useSelector(DependenciesV2Store.selectors.getCount);
  const totalItems = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const setPage = useFetchDependencyAction(DependenciesV2Store.actions.setPage);
  const setCount = useFetchDependencyAction(DependenciesV2Store.actions.setCount);

  // used by the dependency drawer
  const [selectedDependencyRevisionLocator, setSelectedDependencyRevisionLocator] = useState<string | null>(null);
  // used by the remove dependency component
  const [dependencyToRemove, setDependencyToRemove] = useState<DependencyToRemove | null>(null);
  // used to overwrite user dependencies
  const [dependencyLocatorToOverwrite, setDependencyLocatorToOverwrite] = useState<string | null>(null);
  // used by edit dependency component - see: https://github.com/fossas/FOSSA/pull/16861
  const [dependencyLocatorToEdit, setDependencyLocatorToEdit] = useState<string | null>(null);
  // used by the add dependency component
  const [manualDependencyAddMode, setManualDependencyAddMode] = (
    useState<ManuallyAddedDependencyRequestKind | null>(null));
  // used to view in progress builds
  const [inProgressBuildId, setInProgressBuildId] = useState<number | null>(null);

  const onRemoveProjectDependency = useCallback((dependencyRevisionLocator: string) => {
    // Close the drawer and reset the selected dependency revision locator
    setSelectedDependencyRevisionLocator(null);
    // And reset the depedency to remove
    setDependencyToRemove(null);
    // Remove the dependency from the watch list to prevent unnecessary builds
    dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependencyRevisionLocator));
    // Trigger a project reload to refresh the dependencies on the page
    onProjectChange();
  }, [setSelectedDependencyRevisionLocator, dispatch, onProjectChange]);

  useDependencyDrawer({
    scope,
    selectedDependencyRevisionLocator,
    setSelectedDependencyRevisionLocator,
    onDeleteProjectDependency: onRemoveProjectDependency,
  });

  // If the current user is authenticated, check if they belong to the current scope target's org.
  useEffect(() => {
    if (window.user) {
      // Check if the user is logged in and belongs to the project's org.
      if (project && window.user.organizationId === project.data?.organizationId) {
        dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
      }
      // Check if the user is logged in and belongs to the project group's org.
      if (projectGroup && window.user.organizationId === projectGroup.organizationId) {
        dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
      }
    }
  }, [dispatch, project, projectGroup]);

  useSetDependencyScope({
    projectId,
    revisionId,
    projectGroup,
    projectGroupRelease,
  });

  useOnUnmount(() => {
    dispatch(DependenciesV2Store.actions.resetState());
  });

  return (
    <div className={cx('container', 'container-responsive', styles.DependenciesContainer)}>
      <SidebarLayout>
        <SidebarLayout.Header>
          <DependenciesHeader setManualDependencyAddMode={setManualDependencyAddMode} />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <DependenciesTable
            modals={modals}
            setDependencyToRemove={setDependencyToRemove}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
            setManualDependencyAddMode={setManualDependencyAddMode}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
            setInProgressBuildId={setInProgressBuildId}
            setDependencyLocatorToEdit={setDependencyLocatorToEdit}
          />
          <PaginationFooter
            page={page}
            count={count}
            totalItems={totalItems}
            setPage={setPage}
            setCount={setCount}
            countOptions={countOptions}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <DependencyFilters
            shouldDisplayConfidenceFilter={isBinaryProject || isReleaseGroupWithBinaryProject}
            shouldDisplayDependencySourceFilter={isReleaseGroupScope && isVendoredDepsEnabled}
            isContainerProject={isContainerProject}
            isReleaseGroupScope={isReleaseGroupScope}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      <StreamBuild buildId={inProgressBuildId} setBuildId={setInProgressBuildId} />
      {/*
        TODO: Remove EditDependency once we resolve the related customer issue,
        see: https://github.com/fossas/FOSSA/pull/16861
      */}
      <EditDependency
        userRevisionLocator={projectRevision}
        dependencyLocatorToEdit={dependencyLocatorToEdit}
        setDependencyLocatorToEdit={setDependencyLocatorToEdit}
      />
      {/* The following modals only apply to project dependencies. */}
      {scope && scope.scope === 'project' && (
        <>
          <ViewRelationships projectRevision={scope.revision} modals={modals} />
          <AddDependency
            userRevisionLocator={scope.revision}
            manualDependencyAddMode={manualDependencyAddMode}
            setManualDependencyAddMode={setManualDependencyAddMode}
            dependencyLocatorToOverwrite={dependencyLocatorToOverwrite}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
          />
          <RemoveDependency
            userRevisionLocator={scope.revision}
            dependencyToRemove={dependencyToRemove}
            setDependencyToRemove={setDependencyToRemove}
            onRemoveProjectDependency={onRemoveProjectDependency}
          />
          <ModalManagePackageLabels
            show={modals.managePackageLabelsModal.isOpen}
            onHide={modals.managePackageLabelsModal.onClose}
            context={modals.managePackageLabelsModal.context}
            parentRevisionLocator={projectRevision}
            isGlobalEnabled
            isProjectEnabled
            isRevisionEnabled
          />
          <BinaryMatchDetailsModalContainer modals={modals} projectRevision={scope.revision} />
        </>
      )}
    </div>
  );
};

export default DependenciesV2;
